import { useState, type FormEvent } from 'react';
import type {
  AttributeProfileResponse,
  DevelopmentActionDetail,
  DevelopmentActionStatus,
  DevelopmentPlanDetail,
  DevelopmentPlansResponse,
  EmployeeProfileResponse,
  RoleListResponse,
} from '../../api/types';
import { apiPatch, apiPost, describeError, getDevEmployeeId } from '../../api/client';
import { useApiResource, type ResourceState } from '../../hooks/useApiResource';
import { FactSection } from './FactSection';
import { formatCalendarDate } from './attributeDisplay';
import factStyles from './FactRecords.module.css';
import formStyles from './AssessmentForm.module.css';
import styles from './CaptureSection.module.css';

/**
 * Development — basic capture (docs/DOMAIN_DECISIONS.md, "Development and
 * Career Aspiration Capture Scope"; `UX_ARCHITECTURE.md` §3.7).
 *
 * A flat list of `DevelopmentPlan`s, each holding a flat list of
 * `DevelopmentAction`s (DC-2) — no target-capability derivation, no
 * automatic gap detection, no reassessment linkage beyond the one real
 * foreign key (`linkedAttributeDefinitionId`) DC-2 already permits. The
 * full gap-driven loop (`UX_ARCHITECTURE.md` §3.7's "POST-MVP") is not
 * built here and nothing in this file anticipates it.
 *
 * Reading is the same broad gate every other fact section uses — a manager
 * can read a report's development plan exactly as they can read their
 * skills. Creating or editing a plan or action is narrower: only the
 * employee whose plan it is may do either (DC-3, `authorizeSelfWrite`),
 * which this component enforces only as a display decision — the API
 * enforces it as the actual control, and refuses regardless of what this
 * file decides to show.
 */
export function DevelopmentSection({
  profile,
  roles,
  attributes,
}: {
  profile: EmployeeProfileResponse;
  roles: ResourceState<RoleListResponse>;
  attributes: ResourceState<AttributeProfileResponse>;
}) {
  const isSelf = getDevEmployeeId() === profile.id;
  const plans = useApiResource<DevelopmentPlansResponse>(`/employees/${profile.id}/development-plans`);
  const [creatingPlan, setCreatingPlan] = useState(false);

  function roleName(id: string | null): string | null {
    if (id === null || roles.status !== 'loaded') return null;
    const role = roles.data.roles.find((candidate) => candidate.id === id);
    return role ? (role.status === 'retired' ? `${role.name} (retired)` : role.name) : null;
  }

  return (
    <>
      <FactSection<DevelopmentPlansResponse>
        id="development"
        title="Development"
        state={plans}
        loadingLabel="Loading development plans…"
        errorTitle="Development plans could not be loaded"
        isEmpty={(data) => data.plans.length === 0}
        empty={{
          title: 'No development plans recorded',
          detail: isSelf
            ? 'You have not recorded a development plan yet. Use "Add a development plan" below to start one.'
            : 'Nothing has been recorded here yet. That is an absence of records, not a finding about this person.',
        }}
        summary={(data) => `${data.plans.length} ${data.plans.length === 1 ? 'plan' : 'plans'} recorded`}
        note="A flat record of development plans and the actions under them — not the full gap-driven development loop. Only the person a plan belongs to may add to it or edit it."
      >
        {(data) => (
          <ul className={styles.list}>
            {data.plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                employeeId={profile.id}
                isSelf={isSelf}
                roleName={roleName}
                roles={roles}
                attributes={attributes}
                onChanged={() => plans.reload()}
              />
            ))}
          </ul>
        )}
      </FactSection>

      {isSelf && plans.status === 'loaded' ? (
        creatingPlan ? (
          <NewPlanForm
            employeeId={profile.id}
            roles={roles}
            onCreated={() => {
              setCreatingPlan(false);
              plans.reload();
            }}
            onClose={() => setCreatingPlan(false)}
          />
        ) : (
          <button type="button" className={styles.addToggle} onClick={() => setCreatingPlan(true)}>
            Add a development plan
          </button>
        )
      ) : null}
    </>
  );
}

function PlanCard({
  plan,
  employeeId,
  isSelf,
  roleName,
  roles,
  attributes,
  onChanged,
}: {
  plan: DevelopmentPlanDetail;
  employeeId: string;
  isSelf: boolean;
  roleName: (id: string | null) => string | null;
  roles: ResourceState<RoleListResponse>;
  attributes: ResourceState<AttributeProfileResponse>;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [addingAction, setAddingAction] = useState(false);
  const targetRoleName = roleName(plan.targetRoleId);

  return (
    <li className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.name}>{plan.name ?? <span className={styles.absent}>Untitled plan</span>}</span>
        {plan.targetRoleId ? (
          <span className={styles.status}>Towards {targetRoleName ?? 'a role'}</span>
        ) : null}
        {isSelf ? (
          <button type="button" className={styles.editToggle} onClick={() => setEditing((open) => !open)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        ) : null}
      </div>

      {editing ? (
        <EditPlanForm
          employeeId={employeeId}
          plan={plan}
          roles={roles}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
          onClose={() => setEditing(false)}
        />
      ) : null}

      {plan.actions.length === 0 ? (
        <p className={factStyles.absent}>No actions recorded under this plan yet.</p>
      ) : (
        <ul className={styles.actions}>
          {plan.actions.map((action) => (
            <ActionRow
              key={action.id}
              action={action}
              employeeId={employeeId}
              isSelf={isSelf}
              attributes={attributes}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}

      {isSelf ? (
        addingAction ? (
          <NewActionForm
            employeeId={employeeId}
            planId={plan.id}
            attributes={attributes}
            onCreated={() => {
              setAddingAction(false);
              onChanged();
            }}
            onClose={() => setAddingAction(false)}
          />
        ) : (
          <button type="button" className={styles.addToggle} onClick={() => setAddingAction(true)}>
            Add an action
          </button>
        )
      ) : null}
    </li>
  );
}

const ACTION_STATUS_LABEL: Record<DevelopmentActionStatus, string> = {
  planned: 'Planned',
  in_progress: 'In progress',
  completed: 'Completed',
  abandoned: 'Abandoned',
};

function attributeName(attributes: ResourceState<AttributeProfileResponse>, id: string | null): string | null {
  if (id === null || attributes.status !== 'loaded') return null;
  const match = attributes.data.attributes.find((state) => state.attribute.id === id);
  return match ? match.attribute.name : null;
}

function ActionRow({
  action,
  employeeId,
  isSelf,
  attributes,
  onChanged,
}: {
  action: DevelopmentActionDetail;
  employeeId: string;
  isSelf: boolean;
  attributes: ResourceState<AttributeProfileResponse>;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const linkedName = attributeName(attributes, action.linkedAttributeDefinitionId);

  return (
    <li className={styles.actionRow}>
      <div className={styles.actionHeader}>
        <span className={styles.description}>{action.actionDescription}</span>
        <span className={styles.status}>{ACTION_STATUS_LABEL[action.status]}</span>
        {isSelf ? (
          <button type="button" className={styles.editToggle} onClick={() => setEditing((open) => !open)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        ) : null}
      </div>

      <dl className={styles.facts}>
        <div>
          <dt>Start</dt>
          <dd>{action.startDate ? formatCalendarDate(action.startDate) : <span className={styles.absent}>Not set</span>}</dd>
        </div>
        <div>
          <dt>Target end</dt>
          <dd>
            {action.targetEndDate ? formatCalendarDate(action.targetEndDate) : <span className={styles.absent}>Not set</span>}
          </dd>
        </div>
        {action.actualEndDate ? (
          <div>
            <dt>Actual end</dt>
            <dd>{formatCalendarDate(action.actualEndDate)}</dd>
          </div>
        ) : null}
        {linkedName ? (
          <div>
            <dt>Relates to</dt>
            <dd>{linkedName}</dd>
          </div>
        ) : null}
      </dl>

      {action.outcome ? <p className={styles.notes}>{action.outcome}</p> : null}

      {editing ? (
        <EditActionForm
          employeeId={employeeId}
          action={action}
          attributes={attributes}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </li>
  );
}

/* ---------------------------------------------------------------------
 * FORMS
 *
 * Shaped after AssessmentForm.tsx's own pattern: local field state, a
 * small submit-state union, and a re-read (never a local patch) on success
 * — every one of these fields is a directly-recorded fact, but the parent
 * list is still the authoritative source after a write.
 * ------------------------------------------------------------------- */

type SubmitState = { status: 'editing' } | { status: 'submitting' } | { status: 'failed'; message: string };

function RoleSelect({
  id,
  value,
  onChange,
  roles,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  roles: ResourceState<RoleListResponse>;
}) {
  return (
    <select id={id} className={styles.select} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">No target role</option>
      {roles.status === 'loaded'
        ? roles.data.roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.status === 'retired' ? `${role.name} (retired)` : role.name}
            </option>
          ))
        : null}
    </select>
  );
}

function NewPlanForm({
  employeeId,
  roles,
  onCreated,
  onClose,
}: {
  employeeId: string;
  roles: ResourceState<RoleListResponse>;
  onCreated: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [targetRoleId, setTargetRoleId] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/development-plans`, {
        name: name.trim() || undefined,
        targetRoleId: targetRoleId || undefined,
      });
      onCreated();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label="Add a development plan">
      <div className={formStyles.field}>
        <label htmlFor="new-plan-name" className={formStyles.label}>
          Name <span className={formStyles.optional}>optional</span>
        </label>
        <input
          id="new-plan-name"
          className={formStyles.text}
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={submitting}
          maxLength={200}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="new-plan-role" className={formStyles.label}>
          Target role <span className={formStyles.optional}>optional</span>
        </label>
        <RoleSelect id="new-plan-role" value={targetRoleId} onChange={setTargetRoleId} roles={roles} />
      </div>
      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Adding…' : 'Add plan'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function EditPlanForm({
  employeeId,
  plan,
  roles,
  onSaved,
  onClose,
}: {
  employeeId: string;
  plan: DevelopmentPlanDetail;
  roles: ResourceState<RoleListResponse>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(plan.name ?? '');
  const [targetRoleId, setTargetRoleId] = useState(plan.targetRoleId ?? '');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setState({ status: 'submitting' });
    try {
      await apiPatch(`/employees/${employeeId}/development-plans/${plan.id}`, {
        name: name.trim() || null,
        targetRoleId: targetRoleId || null,
      });
      onSaved();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label={`Edit ${plan.name ?? 'this plan'}`}>
      <div className={formStyles.field}>
        <label htmlFor={`edit-plan-name-${plan.id}`} className={formStyles.label}>
          Name
        </label>
        <input
          id={`edit-plan-name-${plan.id}`}
          className={formStyles.text}
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={submitting}
          maxLength={200}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-plan-role-${plan.id}`} className={formStyles.label}>
          Target role
        </label>
        <RoleSelect id={`edit-plan-role-${plan.id}`} value={targetRoleId} onChange={setTargetRoleId} roles={roles} />
      </div>
      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function AttributeSelect({
  id,
  value,
  onChange,
  attributes,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  attributes: ResourceState<AttributeProfileResponse>;
}) {
  return (
    <select id={id} className={styles.select} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">Not linked to an attribute</option>
      {attributes.status === 'loaded'
        ? attributes.data.attributes.map((state) => (
            <option key={state.attribute.id} value={state.attribute.id}>
              {state.attribute.name}
            </option>
          ))
        : null}
    </select>
  );
}

function NewActionForm({
  employeeId,
  planId,
  attributes,
  onCreated,
  onClose,
}: {
  employeeId: string;
  planId: string;
  attributes: ResourceState<AttributeProfileResponse>;
  onCreated: () => void;
  onClose: () => void;
}) {
  const [actionDescription, setActionDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [targetEndDate, setTargetEndDate] = useState('');
  const [linkedAttributeDefinitionId, setLinkedAttributeDefinitionId] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const valid = actionDescription.trim().length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !valid) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/development-plans/${planId}/actions`, {
        actionDescription: actionDescription.trim(),
        startDate: startDate || undefined,
        targetEndDate: targetEndDate || undefined,
        linkedAttributeDefinitionId: linkedAttributeDefinitionId || undefined,
      });
      onCreated();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label="Add an action">
      <div className={formStyles.field}>
        <label htmlFor={`new-action-description-${planId}`} className={formStyles.label}>
          Action
        </label>
        <textarea
          id={`new-action-description-${planId}`}
          className={formStyles.textarea}
          rows={2}
          value={actionDescription}
          onChange={(event) => setActionDescription(event.target.value)}
          disabled={submitting}
          maxLength={2000}
          required
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`new-action-start-${planId}`} className={formStyles.label}>
          Start date <span className={formStyles.optional}>optional</span>
        </label>
        <input
          id={`new-action-start-${planId}`}
          className={formStyles.text}
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`new-action-end-${planId}`} className={formStyles.label}>
          Target end date <span className={formStyles.optional}>optional</span>
        </label>
        <input
          id={`new-action-end-${planId}`}
          className={formStyles.text}
          type="date"
          value={targetEndDate}
          onChange={(event) => setTargetEndDate(event.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`new-action-attribute-${planId}`} className={formStyles.label}>
          Relates to <span className={formStyles.optional}>optional</span>
        </label>
        <AttributeSelect
          id={`new-action-attribute-${planId}`}
          value={linkedAttributeDefinitionId}
          onChange={setLinkedAttributeDefinitionId}
          attributes={attributes}
        />
      </div>
      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !valid}>
          {submitting ? 'Adding…' : 'Add action'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const ACTION_STATUS_OPTIONS: DevelopmentActionStatus[] = ['planned', 'in_progress', 'completed', 'abandoned'];

function EditActionForm({
  employeeId,
  action,
  attributes,
  onSaved,
  onClose,
}: {
  employeeId: string;
  action: DevelopmentActionDetail;
  attributes: ResourceState<AttributeProfileResponse>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<DevelopmentActionStatus>(action.status);
  const [startDate, setStartDate] = useState(action.startDate ?? '');
  const [targetEndDate, setTargetEndDate] = useState(action.targetEndDate ?? '');
  const [actualEndDate, setActualEndDate] = useState(action.actualEndDate ?? '');
  const [outcome, setOutcome] = useState(action.outcome ?? '');
  const [linkedAttributeDefinitionId, setLinkedAttributeDefinitionId] = useState(
    action.linkedAttributeDefinitionId ?? ''
  );
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setState({ status: 'submitting' });
    try {
      await apiPatch(`/employees/${employeeId}/development-plans/${action.developmentPlanId}/actions/${action.id}`, {
        status,
        startDate: startDate || null,
        targetEndDate: targetEndDate || null,
        actualEndDate: actualEndDate || null,
        outcome: outcome.trim() || null,
        linkedAttributeDefinitionId: linkedAttributeDefinitionId || null,
      });
      onSaved();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label={`Edit ${action.actionDescription}`}>
      <div className={formStyles.field}>
        <label htmlFor={`edit-action-status-${action.id}`} className={formStyles.label}>
          Status
        </label>
        <select
          id={`edit-action-status-${action.id}`}
          className={styles.select}
          value={status}
          onChange={(event) => setStatus(event.target.value as DevelopmentActionStatus)}
          disabled={submitting}
        >
          {ACTION_STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {ACTION_STATUS_LABEL[option]}
            </option>
          ))}
        </select>
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-action-start-${action.id}`} className={formStyles.label}>
          Start date
        </label>
        <input
          id={`edit-action-start-${action.id}`}
          className={formStyles.text}
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-action-target-end-${action.id}`} className={formStyles.label}>
          Target end date
        </label>
        <input
          id={`edit-action-target-end-${action.id}`}
          className={formStyles.text}
          type="date"
          value={targetEndDate}
          onChange={(event) => setTargetEndDate(event.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-action-actual-end-${action.id}`} className={formStyles.label}>
          Actual end date
        </label>
        <input
          id={`edit-action-actual-end-${action.id}`}
          className={formStyles.text}
          type="date"
          value={actualEndDate}
          onChange={(event) => setActualEndDate(event.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-action-outcome-${action.id}`} className={formStyles.label}>
          Outcome <span className={formStyles.optional}>optional</span>
        </label>
        <textarea
          id={`edit-action-outcome-${action.id}`}
          className={formStyles.textarea}
          rows={2}
          value={outcome}
          onChange={(event) => setOutcome(event.target.value)}
          disabled={submitting}
          maxLength={2000}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-action-attribute-${action.id}`} className={formStyles.label}>
          Relates to <span className={formStyles.optional}>optional</span>
        </label>
        <AttributeSelect
          id={`edit-action-attribute-${action.id}`}
          value={linkedAttributeDefinitionId}
          onChange={setLinkedAttributeDefinitionId}
          attributes={attributes}
        />
      </div>
      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
